import React, {useRef, useState, useEffect} from "react";
import Button from "../layout/Button.jsx";
import {useLocale} from "../locale/index.js";

export default function SearchTabs({
  type = "all",
  onTypeChange,
  types = [],
  counts = {},
  onOpenFilters,
  activeFilterCount = 0,
  filtersLabel = "Filters",
  filtersOpen = false,
}) {
  const {getString, formatString} = useLocale();
  const typeLabels = React.useMemo(
    () => ({
      work: getString("common.types.work", "Works"),
      page: getString("common.types.page", "Pages"),
      docs: getString("common.types.docs", "Docs"),
    }),
    [getString],
  );
  const allTypesLabel = getString("common.nouns.items", "items");
  const orderedTypes = Array.isArray(types) ? types : [];
  const toLabel = (t) => {
    const key = String(t || "").toLowerCase();
    if (!key) return "";
    if (key === "all") {
      return allTypesLabel ? String(allTypesLabel) : "All";
    }
    if (typeLabels[key]) return typeLabels[key];
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  const hasFilters = typeof onOpenFilters === "function";
  const resolvedFiltersLabel =
    typeof filtersLabel === "string" && filtersLabel.trim()
      ? filtersLabel
      : getString("common.nouns.filters", "Filters");
  const filterBadge =
    activeFilterCount > 0 ? (
      <span className="canopy-search-tabs__filters-count">
        ({activeFilterCount})
      </span>
    ) : null;
  const searchTypesLabel = formatString(
    "common.phrases.search_content",
    "Search types",
    {content: getString("common.nouns.types", "types")},
  );

  // --- highlight state (ported from RadioGroup idea) ---
  const [itemBoundingBox, setItemBoundingBox] = useState(null);
  const [wrapperBoundingBox, setWrapperBoundingBox] = useState(null);
  const [highlightedTab, setHighlightedTab] = useState(null);
  const [isHoveredFromNull, setIsHoveredFromNull] = useState(true);

  const wrapperRef = useRef(null);
  const highlightRef = useRef(null);

  const repositionHighlight = (e, tabKey) => {
    if (!tabKey || !wrapperRef.current) return;

    // use currentTarget so we get the <button>, not the inner text node
    const item = e.currentTarget;
    setItemBoundingBox(item.getBoundingClientRect());
    setWrapperBoundingBox(wrapperRef.current.getBoundingClientRect());
    setIsHoveredFromNull(!highlightedTab);
    setHighlightedTab(tabKey);
  };

  const resetHighlight = () => {
    setHighlightedTab(null);
  };

  // Optional: snap highlight to the active tab when `type` changes
  useEffect(() => {
    if (!wrapperRef.current) return;
    const activeButton = wrapperRef.current.querySelector(
      `button[data-tab-value="${type}"]`
    );
    if (!activeButton) return;

    const itemBox = activeButton.getBoundingClientRect();
    const wrapperBox = wrapperRef.current.getBoundingClientRect();

    setItemBoundingBox(itemBox);
    setWrapperBoundingBox(wrapperBox);
    setIsHoveredFromNull(true);
    setHighlightedTab(type);
  }, [type]);

  let highlightStyles = {};
  if (itemBoundingBox && wrapperBoundingBox) {
    highlightStyles = {
      opacity: highlightedTab ? 1 : 0,
      transform: `translateX(${itemBoundingBox.left - wrapperBoundingBox.left}px)`,
      transitionDuration: isHoveredFromNull ? "0ms" : "200ms",
      width: `${itemBoundingBox.width}px`,
    };
  }

  return (
    <div className="canopy-search-tabs-wrapper">
      <div
        role="tablist"
        aria-label={searchTypesLabel || "Search types"}
        className="canopy-search-tabs"
        ref={wrapperRef}
        onMouseLeave={resetHighlight}
      >
        {/* moving highlight */}
        <div
          ref={highlightRef}
          className="canopy-search-tabs__highlight"
          style={highlightStyles}
        />

        {orderedTypes.map((t) => {
          const active = String(type).toLowerCase() === String(t).toLowerCase();
          const cRaw =
            counts && Object.prototype.hasOwnProperty.call(counts, t)
              ? counts[t]
              : undefined;
          const c = Number.isFinite(Number(cRaw)) ? Number(cRaw) : 0;
          return (
            <button
              key={t}
              role="tab"
              aria-selected={active}
              type="button"
              data-tab-value={t}
              onClick={() => onTypeChange && onTypeChange(t)}
              onMouseOver={(e) => repositionHighlight(e, t)}
              className={`canopy-search-tab ${active ? "is-active" : ""}`}
            >
              {toLabel(t)} ({c})
            </button>
          );
        })}
      </div>

      {hasFilters ? (
        <Button
          as="button"
          type="button"
          variant="primary"
          onClick={() => onOpenFilters && onOpenFilters()}
          aria-expanded={filtersOpen ? "true" : "false"}
          className="canopy-search-tabs__filters-button"
        >
          <span>{resolvedFiltersLabel}</span>
          {filterBadge}
        </Button>
      ) : null}
    </div>
  );
}
